import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import {
  StatusBadge,
  conferenceStatusTone,
  destinationLifecycleTone,
} from "@/components/status/status-badge";
import { ConferenceConfigurationPanel } from "@/features/conferences/conference-configuration";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import type { Conference, Destination } from "@/lib/types";

type Tab =
  "overview" | "destinations" | "activity" | "configuration" | "billing";

export function ConferenceDetailPage() {
  const { orgId, canManage } = useOrg();
  const { conferenceId = "" } = useParams();
  const [conference, setConference] = useState<Conference | null>(null);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [activity, setActivity] = useState<
    Array<{
      id: string;
      title: string;
      detail?: string;
      createdAt: number;
      status: string;
    }>
  >([]);
  const [tab, setTab] = useState<Tab>("overview");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void Promise.all([
      api<{ conference: Conference }>(
        `/api/accounts/${orgId}/conferences/${conferenceId}`,
      ),
      api<{ streams: Destination[] }>(
        `/api/accounts/${orgId}/conferences/${conferenceId}/streams`,
      ),
      api<{ items: typeof activity }>(
        `/api/accounts/${orgId}/activity?conferenceId=${encodeURIComponent(conferenceId)}&limit=25`,
      ).catch(() => ({ items: [] })),
    ])
      .then(([c, s, a]) => {
        setConference(c.conference);
        setDestinations(s.streams);
        setActivity(a.items ?? []);
      })
      .catch(() => {
        setConference(null);
        setDestinations([]);
      })
      .finally(() => setLoading(false));
  }, [orgId, conferenceId]);

  if (loading) return <PageSkeleton />;
  if (!conference) {
    return <p className="text-sm text-rs-danger">Conference not found.</p>;
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "destinations", label: "Destinations" },
    { id: "activity", label: "Activity" },
    { id: "configuration", label: "Configuration" },
    { id: "billing", label: "Billing" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm text-rs-ink-muted">
            <Link to={`/conferences`} className="hover:underline">
              Conferences
            </Link>{" "}
            /
          </p>
          <h2 className="text-xl font-semibold text-rs-ink">
            {conference.name}
          </h2>
          <div className="mt-2">
            <StatusBadge tone={conferenceStatusTone(conference.status)}>
              {conference.status}
            </StatusBadge>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-rs-border pb-2">
        {tabs.map((t) => (
          <Button
            key={t.id}
            size="sm"
            variant={tab === t.id ? "primary" : "ghost"}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {tab === "overview" ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel className="p-5">
            <h3 className="font-semibold text-rs-ink">Summary</h3>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-rs-ink-muted">Billing status</dt>
                <dd className="capitalize text-rs-ink">{conference.status}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-rs-ink-muted">Destinations</dt>
                <dd className="text-rs-ink">{destinations.length}</dd>
              </div>
            </dl>
          </Panel>
          <Panel className="p-5">
            <h3 className="font-semibold text-rs-ink">Destination Health</h3>
            <ul className="mt-3 space-y-2">
              {destinations.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <span className="capitalize text-rs-ink">
                    {d.destinationProvider}
                  </span>
                  <StatusBadge tone={destinationLifecycleTone(d.lifecycle)}>
                    {d.lastErrorCode ? "Failed" : d.lifecycle}
                  </StatusBadge>
                </li>
              ))}
              {destinations.length === 0 ? (
                <li className="text-sm text-rs-ink-muted">
                  No destinations configured.
                </li>
              ) : null}
            </ul>
          </Panel>
        </div>
      ) : null}

      {tab === "destinations" ? (
        <Panel>
          <PanelHeader
            title="Destinations"
            description="Each destination syncs independently"
          />
          <ul className="divide-y divide-rs-border">
            {destinations.map((d) => (
              <li key={d.id} className="px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium capitalize text-rs-ink">
                      {d.destinationProvider}
                    </p>
                    <p className="text-sm text-rs-ink-muted">{d.name}</p>
                  </div>
                  <StatusBadge tone={destinationLifecycleTone(d.lifecycle)}>
                    {d.lastErrorCode ? "Failed" : d.lifecycle}
                  </StatusBadge>
                </div>
                {d.remediation ? (
                  <p className="mt-2 text-sm text-rs-warning">
                    {d.remediation.title}: {d.remediation.action}
                  </p>
                ) : null}
                {canManage ? (
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="secondary"
                    onClick={() => setTab("configuration")}
                  >
                    Configure
                  </Button>
                ) : null}
              </li>
            ))}
            {destinations.length === 0 ? (
              <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                No destinations yet.
              </li>
            ) : null}
          </ul>
        </Panel>
      ) : null}

      {tab === "activity" ? (
        <Panel>
          <PanelHeader
            title="Activity"
            description="Recent sync events for this conference"
          />
          <ul className="divide-y divide-rs-border">
            {activity.map((item) => (
              <li key={item.id} className="px-5 py-3 text-sm">
                <p className="font-medium text-rs-ink">{item.title}</p>
                {item.detail ? (
                  <p className="text-rs-ink-muted">{item.detail}</p>
                ) : null}
              </li>
            ))}
            {activity.length === 0 ? (
              <li className="px-5 py-8 text-center text-sm text-rs-ink-muted">
                No activity yet.
              </li>
            ) : null}
          </ul>
        </Panel>
      ) : null}

      {tab === "configuration" ? (
        <ConferenceConfigurationPanel
          orgId={orgId}
          conferenceId={conferenceId}
          canManage={canManage}
        />
      ) : null}

      {tab === "billing" ? (
        <Panel className="p-5">
          <h3 className="font-semibold text-rs-ink">Account Billing</h3>
          <p className="mt-2 text-sm text-rs-ink-muted">
            Billing is account-level: $10 per billable active stream per month
            as subscription seats (quantity updates with prorations), plus $0.05
            per attendee processed (metered on the invoice). Manage trial usage,
            upgrades, and invoices from Settings.
          </p>
          <p className="mt-3 text-sm">
            Conference status:{" "}
            <span className="font-medium capitalize text-rs-ink">
              {conference.status}
            </span>
          </p>
          <Button className="mt-4" asChild>
            <Link to="/settings/billing">Open Settings Billing</Link>
          </Button>
        </Panel>
      ) : null}
    </div>
  );
}
